/**
 * The rules of Settings › Iris (CU-869fkmt6r): the draft over the server's profile, what is dirty,
 * how many changes, the limits, the footer's and the preview's lines. Pure — no React, no signals,
 * no palette — so `node --test` runs it. The server owns the profile (`profile.py` `_sanitise`);
 * the screen only ever paints its answer.
 */
import { hasEditableProfile } from "../../../../../@utils/irisProfile.js"

export const EMPTY_PROFILE = Object.freeze({
	lang: null,
	voice: { preset: null, notes: '' },
	compset: { tier: null, provider_ids: [] },
	target_rank: null,
	focus: {
		offices: [], lors: [], acriss_classes: [], acriss_list: [],
		channels: { site_ids: [], kind: null, group: null },
	},
	briefing: { on: true },
})

// The server's caps (`profile.py` MAX_*)
export const LIMITS = Object.freeze({ notes: 600, offices: 12, lors: 6, codes: 24, sites: 20, rank: 20 })

export const EDITABLE_KEYS = Object.freeze(['lang', 'voice', 'compset', 'target_rank', 'focus', 'briefing'])

const list = (v) => (Array.isArray(v) ? v : [])
const orNull = (v) => (v === undefined || v === '' ? null : v)

/** The server's profile filled to the full shape; `own` / `compare` kept as they came (`null` if absent). */
export function fromServer(payload) {
	const p = payload && typeof payload === 'object' ? payload : {}
	const voice = p.voice || {}
	const compset = p.compset || {}
	const focus = p.focus || {}
	const channels = focus.channels || {}
	return {
		lang: orNull(p.lang) ?? null,
		voice: { preset: orNull(voice.preset) ?? null, notes: voice.notes ?? '' },
		compset: { tier: orNull(compset.tier) ?? null, provider_ids: [...list(compset.provider_ids)] },
		target_rank: p.target_rank ?? null,
		focus: {
			offices: list(focus.offices).map(o => ({ id: o?.id, label: o?.label ?? null })),
			lors: [...list(focus.lors)],
			acriss_classes: [...list(focus.acriss_classes)],
			acriss_list: [...list(focus.acriss_list)],
			channels: { site_ids: [...list(channels.site_ids)], kind: orNull(channels.kind) ?? null, group: orNull(channels.group) ?? null },
		},
		briefing: { on: p.briefing?.on ?? true },
		own: p.own ?? null,
		compare: p.compare ?? null,
	}
}

/**
 * The `PUT` body: the whole editable profile — `briefing` and `compset.provider_ids` travel as they
 * were loaded (criteria 12, 13) — and never `own`, `compare` or anything computed by the server.
 */
export function toPayload(draft) {
	const p = fromServer(draft)
	return {
		lang: p.lang,
		voice: { ...p.voice },
		compset: { tier: p.compset.tier, provider_ids: [...p.compset.provider_ids] },
		target_rank: p.target_rank,
		focus: {
			offices: p.focus.offices.map(o => ({ id: o.id, label: o.label })),
			lors: [...p.focus.lors],
			acriss_classes: [...p.focus.acriss_classes],
			acriss_list: [...p.focus.acriss_list],
			channels: { ...p.focus.channels, site_ids: [...p.focus.channels.site_ids] },
		},
		briefing: { ...p.briefing },
	}
}

const sorted = (l) => [...l].sort((a, b) => (a > b ? 1 : a < b ? -1 : 0))

/**
 * The editable part in a fixed key order, sets sorted (the server sorts them; `_sanitise`), offices
 * by id without their labels — so a freshly loaded profile is never dirty whatever the order of the
 * server's keys (the workspace trusted that order, `Settings/index.jsx:97`).
 */
export function canonical(profile) {
	const p = toPayload(profile)
	return {
		lang: p.lang,
		preset: p.voice.preset,
		notes: p.voice.notes,
		compset: { tier: p.compset.tier, provider_ids: sorted(p.compset.provider_ids) },
		target_rank: p.target_rank,
		offices: sorted(p.focus.offices.map(o => o.id)),
		lors: sorted(p.focus.lors),
		cars: { classes: sorted(p.focus.acriss_classes), codes: sorted(p.focus.acriss_list) },
		sites: { site_ids: sorted(p.focus.channels.site_ids), kind: p.focus.channels.kind, group: p.focus.channels.group },
		briefing: p.briefing.on,
	}
}

// The changes as the user counts them: one per first-level group (TL 7)
const GROUPS = ['lang', 'preset', 'notes', 'compset', 'target_rank', 'offices', 'lors', 'cars', 'sites', 'briefing']

export function changeCount(saved, draft) {
	if (!draft) return 0
	const a = canonical(saved)
	const b = canonical(draft)
	return GROUPS.filter(k => JSON.stringify(a[k]) !== JSON.stringify(b[k])).length
}

export const isDirty = (saved, draft) => changeCount(saved, draft) > 0

/** Immutable deep merge of a partial change into the draft; keeps `channels`, `briefing`, `own`, `compare`. */
export function patchProfile(base, next = {}) {
	const b = fromServer(base)
	return {
		...b,
		...next,
		voice: { ...b.voice, ...(next.voice ?? {}) },
		compset: { ...b.compset, ...(next.compset ?? {}) },
		briefing: { ...b.briefing, ...(next.briefing ?? {}) },
		focus: {
			...b.focus,
			...(next.focus ?? {}),
			channels: { ...b.focus.channels, ...(next.focus?.channels ?? {}) },
		},
		own: b.own,
		compare: b.compare,
	}
}

/** The typed rank, clamped to 1–20; empty or not a number is "no target". */
export function clampRank(input) {
	if (input === null || input === undefined || String(input).trim() === '') return null
	const n = Number(input)
	if (!Number.isFinite(n)) return null
	return Math.min(LIMITS.rank, Math.max(1, Math.round(n)))
}

export const atLimit = (items, max) => list(items).length >= max
export const canAdd = (items, max) => !atLimit(items, max)

/** A 4-character ACRISS code, upper-cased; anything else `null`. */
export function isAcrissCode(text) {
	const code = String(text ?? '').trim().toUpperCase()
	return /^[A-Z0-9]{4}$/.test(code) ? code : null
}

/** How the sites are picked: named sites, a site type, or a site group. */
export function sitesMode(channels) {
	if (list(channels?.site_ids).length) return 'sites'
	if (channels?.kind) return 'kind'
	if (channels?.group) return 'group'
	return 'sites'
}

/** Switching how sites are picked clears the other two (criterion 6). */
export function setSitesMode(draft, mode) {
	const c = fromServer(draft).focus.channels
	const channels = mode === 'kind' ? { site_ids: [], kind: c.kind, group: null }
		: mode === 'group' ? { site_ids: [], kind: null, group: c.group }
		: { site_ids: c.site_ids, kind: null, group: null }
	return patchProfile(draft, { focus: { channels } })
}

export const WHOLE_MARKET = 'whole'

/** The compset select: the tier, or the whole market — also for loose provider ids without a tier (criterion 12). */
export const compsetOption = (compset) => compset?.tier || WHOLE_MARKET

/** Picking a tier (or the whole market) clears the loose ids: the server resolves the tier's own. */
export const selectTier = (draft, value) =>
	patchProfile(draft, { compset: { tier: value && value !== WHOLE_MARKET ? value : null, provider_ids: [] } })

/**
 * The footer's line, as `SettingsStickyFooter` takes it (`{ tone, text, count }`). "No profile" looks
 * at what is editable only; with an identity, the identity is what travels meanwhile.
 */
/** The confirm before leaving the Iris page with unsaved edits (CU-869fmherf D3, mock `mock-menu-v1`). */
export function leaveIrisText(count) {
	return {
		title: 'Leave Iris settings?',
		message: `You have ${count} unsaved change${count === 1 ? '' : 's'} in Iris. Nothing is saved by leaving the page.`,
	}
}

export function footerHint({ saved, dirty, changes, error, savedJustNow }) {
	if (error && dirty) return {
		tone: 'error',
		text: `Could not save (${error.status || 'network error'}). Nothing was changed — your edits are still here`,
	}
	if (dirty) return { tone: 'dirty', count: changes, text: 'Not saved yet — Iris keeps answering with the saved version' }
	if (!hasEditableProfile(saved)) return {
		tone: 'muted',
		text: saved?.own
			? "No profile yet — only your identity (Settings › Account) travels to Iris until you save something"
			: 'No profile yet — nothing about you travels to Iris until you save something',
	}
	if (savedJustNow) return { tone: 'ok', text: 'Saved just now' }
	return { tone: 'ok', text: 'Saved · Iris uses the text above with every question' }
}

/** The lines around "What Iris is told" (criterion 10): the server's text is the saved one. */
export function promptHelp({ prompt, saved, dirty }) {
	let help = null
	if (!prompt) help = 'Nothing yet — Iris treats you as a new user. Fill in anything above and save.'
	else if (!hasEditableProfile(saved)) help = "Nothing of yours yet — Iris treats you as a new user. Only your identity (Settings › Account) travels. Fill in anything above and save."
	return { help, dirtyWarning: dirty ? 'You have unsaved changes — this text still shows the saved version.' : null }
}
